import { useId } from 'react';
import { Avatar, Card, Pill, cn } from '@cyphex/ui';
import { Phone, PhoneOff, Users, Video } from 'lucide-react';
import { CallActionButton } from './CallActionButton';
import { QuickReplyMenu } from './QuickReplyMenu';
import type { IncomingCall } from '../types';

export interface IncomingCallPopupProps {
  call: IncomingCall;
  onAccept: () => void;
  onDecline: () => void;
  onReply: (message: string) => void;
  /** Ringing motion is suppressed for stacked (non-head) calls. */
  ringing?: boolean;
  busy?: boolean;
  /** Raised while the quick-reply menu owns Escape, so the host cannot decline. */
  onQuickReplyOpenChange?: (open: boolean) => void;
}

/** The dark navy incoming-call card, matched to the reference screen. */
export function IncomingCallPopup({
  call, onAccept, onDecline, onReply, ringing = true, busy = false, onQuickReplyOpenChange,
}: IncomingCallPopupProps) {
  const titleId = useId();
  /* A forwarded call must SAY it is forwarded. Without this the delegate sees
     a card claiming the caller rang them, when the caller rang somebody whose
     calls come here. */
  const subtitle = call.forwardedFromName
    ? `Forwarded from ${call.forwardedFromName}`
    : call.isGroup
      ? `${call.otherParticipantCount + 1} people`
      : (call.caller.role ?? null);

  return (
    /*
     * FOLLOWS THE THEME, like the message card beside it - it used to be a
     * fixed dark-navy slab whatever the rest of the app looked like. A soft
     * brand glow sits behind the caller instead of the product's name, which a
     * person being rung already knows.
     */
    <Card
      variant="popup-light"
      padding="none"
      role="alertdialog"
      aria-modal="false"
      aria-labelledby={titleId}
      className="relative w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden animate-scale-in"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-brand-gradient-soft opacity-[0.12] dark:opacity-25"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -top-20 left-1/2 h-44 w-44 -translate-x-1/2 rounded-full bg-brand-gradient opacity-25 blur-3xl"
      />

      <div className="relative flex items-center justify-between gap-3 px-5 pt-4">
        <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <span aria-hidden className="relative flex h-2 w-2">
            {ringing ? (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-70 motion-reduce:animate-none" />
            ) : null}
            <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
          </span>
          {call.kind === 'video' ? 'Incoming video call' : 'Incoming call'}
        </span>
        {call.isPriorityCaller ? (
          <Pill tone="warning" size="sm">Priority</Pill>
        ) : null}
      </div>

      <div className="relative flex flex-col items-center px-6 pb-6 pt-6">
        <span className="relative inline-flex">
          {ringing ? (
            <>
              <span
                aria-hidden
                className="absolute inset-0 rounded-pill bg-brand-500/25 animate-pulse-ring motion-reduce:hidden"
              />
              <span
                aria-hidden
                className="absolute inset-0 rounded-pill bg-brand-500/20 animate-pulse-ring [animation-delay:900ms] motion-reduce:hidden"
              />
            </>
          ) : null}
          <Avatar
            name={call.caller.displayName}
            src={call.caller.avatarUrl}
            size="4xl"
            className={cn('relative rounded-pill shadow-lg ring-4 ring-card')}
          />
        </span>
        <h2 id={titleId} className="mt-5 max-w-full truncate text-2xl font-semibold text-foreground">
          {call.caller.displayName}
        </h2>
        {subtitle !== null ? (
          <p className="mt-1 flex items-center gap-1.5 text-base text-muted-foreground">
            {call.isGroup ? <Users size={14} strokeWidth={2} aria-hidden /> : null}
            {subtitle}
          </p>
        ) : null}

        <div className="mt-7 flex w-full items-start justify-center gap-12">
          <CallActionButton label="Decline" icon={PhoneOff} tone="decline" onClick={onDecline} disabled={busy} />
          <CallActionButton
            label="Accept"
            icon={call.kind === 'video' ? Video : Phone}
            tone="accept"
            onClick={onAccept}
            disabled={busy}
          />
        </div>
      </div>

      <footer className="relative flex items-center justify-center border-t border-border bg-subtle/50 px-5 py-2.5">
        <QuickReplyMenu onReply={onReply} disabled={busy} onOpenChange={onQuickReplyOpenChange} />
      </footer>
    </Card>
  );
}
